import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SubscriptionProvider } from "../context/SubscriptionContext";
import SubscriptionGuard from "../components/SubscriptionGuard";
import OwnerMainLayout from "../layouts/MainLayout";
import DevSubscriptionTools from "../components/DevSubscriptionTools";
import AdminRoute from "../components/AdminRoute";
import AdminLayout from "../layouts/AdminLayout";

// ── Eager-loaded (tiny, needed immediately) ──────────────────
import Login        from "../pages/Login";
import Register     from "../pages/Register";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword  from "../pages/reset-password";
import NotFound       from "../pages/NotFound";
import AdminLogin     from "../pages/AdminLogin";
import PrivacyPage from "../pages/Privacy";
import TermsPage from "../pages/Terms";

// Secret admin entry slug — set VITE_ADMIN_ENTRY_SLUG in .env to customise
const ADMIN_SLUG = import.meta.env.VITE_ADMIN_ENTRY_SLUG || "admin-login";

import ReportPreview from "../pages/dev/Reportpreview"

// ── Lazy-loaded owner pages ──────────────────────────────────
const Dashboard    = lazy(() => import("../pages/Dashboard"));
const Agents       = lazy(() => import("../pages/Agents"));
const Products     = lazy(() => import("../pages/Inventory"));
const Transactions = lazy(() => import("../pages/Transactions"));
const Reports      = lazy(() => import("../pages/Reports"));
const Requests     = lazy(() => import("../pages/requests"));
const CashTracker  = lazy(() => import("../pages/CashTracker"));
const Settings     = lazy(() => import("../pages/Settings"));
const PaymentHistory = lazy(() => import("../pages/PaymentHistory"));
const AgentDetail  = lazy(() => import("../pages/AgentDetail"));

// ── Lazy-loaded admin pages ──────────────────────────────────
const AdminDashboard        = lazy(() => import("../pages/admin/AdminDashboard"));
const AdminOwners           = lazy(() => import("../pages/admin/AdminOwners"));
const AdminOwnerDetail      = lazy(() => import("../pages/admin/AdminOwnerDetail"));
const AdminPlans            = lazy(() => import("../pages/admin/AdminPlans"));
const AdminPayments         = lazy(() => import("../pages/admin/AdminPayments"));
const AdminMonitor          = lazy(() => import("../pages/admin/AdminMonitor"));
const AdminLiveTransactions = lazy(() => import("../pages/admin/AdminLiveTransactions"));
const AdminActivity         = lazy(() => import("../pages/admin/AdminActivity"));
const AdminAuditLog         = lazy(() => import("../pages/admin/AdminAuditLog"));

// ── Shared page-level loading fallback ───────────────────────
function PageLoader() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 14,
    }}>
      <div style={{
        width: 32, height: 32,
        border: "3px solid rgba(34,211,238,0.15)",
        borderTopColor: "#22d3ee",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "DM Mono, monospace" }}>
        Loading…
      </div>
    </div>
  );
}

// ── Route guards ─────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight: "100svh", background: "#080c12", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ width: 36, height: 36, border: "3px solid rgba(34,211,238,0.15)", borderTopColor: "#22d3ee", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "DM Mono, monospace" }}>Loading...</div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace />;
  // Wait for profile before allowing through — prevents admin accounts from
  // briefly landing on owner routes (and being ejected by SubscriptionGuard)
  if (!profile) return <LoadingScreen />;
  // Admins have no subscription — owner routes are not for them
  if (profile.role === "admin") {
    const hasMfa = !!sessionStorage.getItem("admin_mfa_nonce");
    return <Navigate to={hasMfa ? "/admin" : `/${ADMIN_SLUG}`} replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <>{children}</>;
  // Session exists but profile not yet loaded — keep children (Login) mounted
  // rather than switching to LoadingScreen. Unmounting Login here would cause
  // setError() to fire on a stale closure after signIn() returns, silently
  // dropping the error message and leaving the user with no feedback.
  if (!profile) return <>{children}</>;
  if (profile.role === "admin") {
    // Admin with a session but no MFA nonce → send to the secret login page to do MFA
    // Admin with session AND nonce → already fully authenticated, go to panel
    const hasMfa = !!sessionStorage.getItem("admin_mfa_nonce");
    return <Navigate to={hasMfa ? "/admin" : `/${ADMIN_SLUG}`} replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export default function OwnerAppRouter() {
  return (
    <BrowserRouter>
      <Routes>

        {/* ── Public routes ────────────────────────────────── */}
        <Route path="/login"           element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register"        element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password"  element={<ResetPassword />} />

        {import.meta.env.DEV && (
        <Route path="__preview/report" element={<ReportPreview />} />
      )}

        {/* ── Legal pages — always public, no auth, no redirects ─ */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms"   element={<TermsPage />} />

        {/* ── Secret admin entry — always accessible so admin can do MFA even with a live session ─ */}
        <Route path={`/${ADMIN_SLUG}`} element={<AdminLogin />} />

        {/* ── Protected owner routes ───────────────────────── */}
        <Route
          element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <SubscriptionGuard>
                  <OwnerMainLayout />
                </SubscriptionGuard>
                <DevSubscriptionTools />
              </SubscriptionProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard"    element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>}    />
          <Route path="/agents"       element={<Suspense fallback={<PageLoader />}><Agents /></Suspense>}       />
          <Route path="/agents/:id"   element={<Suspense fallback={<PageLoader />}><AgentDetail /></Suspense>}  />
          <Route path="/products"     element={<Suspense fallback={<PageLoader />}><Products /></Suspense>}     />
          <Route path="/transactions" element={<Suspense fallback={<PageLoader />}><Transactions /></Suspense>} />
          <Route path="/reports"      element={<Suspense fallback={<PageLoader />}><Reports /></Suspense>}      />
          <Route path="/settings"     element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>}     />
          <Route path="/billing"      element={<Suspense fallback={<PageLoader />}><PaymentHistory /></Suspense>} />
          <Route path="/requests"     element={<Suspense fallback={<PageLoader />}><Requests /></Suspense>}     />
          <Route path="/CashTracker"  element={<Suspense fallback={<PageLoader />}><CashTracker /></Suspense>}  />
        </Route>

        {/* ── Admin routes ──────────────────────────────────── */}
        <Route
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route path="/admin"                     element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>}        />
          <Route path="/admin/monitor"             element={<Suspense fallback={<PageLoader />}><AdminMonitor /></Suspense>}          />
          <Route path="/admin/live/transactions"   element={<Suspense fallback={<PageLoader />}><AdminLiveTransactions /></Suspense>} />
          <Route path="/admin/owners"              element={<Suspense fallback={<PageLoader />}><AdminOwners /></Suspense>}           />
          <Route path="/admin/owners/:id"          element={<Suspense fallback={<PageLoader />}><AdminOwnerDetail /></Suspense>}      />
          <Route path="/admin/plans"               element={<Suspense fallback={<PageLoader />}><AdminPlans /></Suspense>}            />
          <Route path="/admin/payments"            element={<Suspense fallback={<PageLoader />}><AdminPayments /></Suspense>}         />
          <Route path="/admin/activity"            element={<Suspense fallback={<PageLoader />}><AdminActivity /></Suspense>}          />
          <Route path="/admin/audit"               element={<Suspense fallback={<PageLoader />}><AdminAuditLog /></Suspense>}           />
        </Route>

        {/* ── Fallbacks ─────────────────────────────────────── */}
        <Route path="/"  element={<Navigate to="/dashboard" replace />} />
        <Route path="*"  element={<NotFound />} />

      </Routes>
    </BrowserRouter>
  );
}
